// The notifications bell: unread count refreshed every 60 s and after every action, a panel with the latest 20
// (SPEC §7.10).

import { fetchNotifications, fetchUnreadCount, markAllNotificationsRead, markNotificationRead } from "../api/me";
import type { AppNotification, NotificationsPage } from "../api/types";
import type { ApiResult } from "../api/client";
import { t } from "../i18n/i18n";
import { navigate } from "../router";
import { requireUser } from "../session";
import { createButton, createElement, createLink } from "./dom";
import { describeNotification, notificationRoute } from "./notificationText";

const REFRESH_MILLISECONDS: number = 60_000;
let countLabel: HTMLSpanElement | null = null;
let refreshTimer: number | null = null;

/** The bell button with its panel. */
export function buildBell(): HTMLElement {
  const wrapper: HTMLDivElement = createElement("div", "bell");
  const panel: HTMLDivElement = createElement("div", "bell-panel");
  panel.hidden = true;
  countLabel = createElement("span", "bell-count");
  countLabel.hidden = true;
  const toggle: HTMLButtonElement = createButton("", async (): Promise<void> => {
    panel.hidden = panel.hidden === false;
    if (panel.hidden === false) {
      await fillPanel(panel);
    }
  }, "bell-button");
  // On phones only the icon and count show; the name stays for screen readers.
  toggle.setAttribute("aria-label", t("nav.notifications"));
  toggle.append(createBellIcon(), createElement("span", "hide-narrow", t("nav.notifications")), countLabel);
  wrapper.append(toggle, panel);
  startRefreshing();
  return wrapper;
}

const SVG_NAMESPACE = "http://www.w3.org/2000/svg" as const;

/** A small bell drawn in the text colour (an emoji looks different, or tiny, depending on the device's fonts). */
function createBellIcon(): SVGSVGElement {
  const icon: SVGSVGElement = document.createElementNS(SVG_NAMESPACE, "svg");
  icon.setAttribute("viewBox", "0 0 16 16");
  icon.setAttribute("aria-hidden", "true");
  icon.classList.add("bell-icon");
  const shape: SVGPathElement = document.createElementNS(SVG_NAMESPACE, "path");
  shape.setAttribute("d", "M8 1.5a4.5 4.5 0 0 0-4.5 4.5v3L2 11.5v1h12v-1L12.5 9V6A4.5 4.5 0 0 0 8 1.5zM6.5 13.5a1.5 "
    + "1.5 0 0 0 3 0z");
  shape.setAttribute("fill", "currentColor");
  icon.append(shape);
  return icon;
}

/** Fetches the unread count and shows it on the bell. */
export async function refreshUnreadCount(): Promise<void> {
  const result: ApiResult<{ count: number }> = await fetchUnreadCount();
  if (result.ok === false || countLabel === null) {
    return;
  }

  countLabel.textContent = String(result.data.count);
  countLabel.hidden = result.data.count === 0;
}

/** Stops polling (on sign-out). */
export function stopBell(): void {
  if (refreshTimer !== null) {
    window.clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

function startRefreshing(): void {
  stopBell();
  void refreshUnreadCount();
  refreshTimer = window.setInterval((): void => {
    void refreshUnreadCount();
  }, REFRESH_MILLISECONDS);
}

async function fillPanel(panel: HTMLElement): Promise<void> {
  panel.replaceChildren(createElement("p", "muted", t("app.loading")));
  const result: ApiResult<NotificationsPage> = await fetchNotifications(1, false);
  if (result.ok === false) {
    panel.replaceChildren(createElement("p", "", t("app.networkError")));
    return;
  }

  const tools: HTMLDivElement = createElement("div", "toolbar");
  tools.append(createLink("/notifications", t("notifications.seeAll")), createButton(
    t("notifications.markAllRead"), async (): Promise<void> => {
      await markAllNotificationsRead();
      await fillPanel(panel);
    }, "button-link"));
  panel.replaceChildren(tools, ...buildItems(result.data.items));
}

/** One button per notification; clicking marks it read and opens the related screen. */
export function buildItems(items: AppNotification[]): HTMLElement[] {
  if (items.length === 0) {
    return [createElement("p", "muted", t("notifications.empty"))];
  }

  return items.map(buildItem);
}

function buildItem(notification: AppNotification): HTMLElement {
  const className: string = notification.readAt === null ? "notification-item is-unread" : "notification-item";
  const item: HTMLButtonElement = createButton(describeNotification(notification), async (): Promise<void> => {
    await markNotificationRead(notification.id);
    const route: string | null = notificationRoute(notification, requireUser().role === "Teacher");
    if (route !== null) {
      navigate(route);
    }
  }, className);
  return item;
}
